/**
 * Evidence panel.
 *
 * Attaches evidence to ONE assessment answer or ONE remediation plan and lets
 * people prove it has not been altered since.
 *
 * WHAT THE CLIENT CHECKS, AND WHAT IT DOES NOT
 * The file-type / size / count checks in this file are a COURTESY: they save a
 * user from waiting on an upload the server is certain to refuse. They are not
 * a control. The server re-validates every upload by its leading bytes (magic
 * numbers), by extension, by size and by count — nothing here can be relied on
 * to keep a bad file out, and the UI says so rather than implying otherwise.
 *
 * DOWNLOADS
 * The download route needs the bearer token, and a plain <a href> cannot send
 * one. So the file is fetched with the token, held as a Blob, handed to the
 * browser through a temporary object URL — and that URL is REVOKED afterwards.
 * An unrevoked object URL keeps its whole Blob alive until the page unloads, so
 * a session that opens a dozen 2 MB exhibits would otherwise leak ~24 MB.
 *
 * INTEGRITY
 * "Verify integrity" asks the server to re-hash the stored bytes and compare
 * them with the SHA-256 recorded at upload. The result is shown as an explicit
 * three-way outcome (unchanged / MODIFIED / cannot be verified) with text, an
 * icon and a colour — never colour alone.
 */

import { useCallback, useEffect, useRef, useState, type DragEvent, type KeyboardEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ALLOWED_UPLOAD_TYPES, MAX_UPLOAD_BYTES, MAX_EVIDENCE_FILES_PER_ANSWER,
  type EvidenceLifecycleState, type EvidenceLifecycleSummary,
} from '@opencomply/shared';
import { api, qs, ApiError, getAccessToken } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useApiError } from '../lib/useApiError';
import { cn } from '../lib/cn';
import { Button, Modal, Skeleton, ErrorNotice, ProgressBar, Spinner } from './ui';
import { EvidenceExpiryAlert, LifecycleBadge, ValidityEditor } from './EvidenceLifecycle';

// ---------------------------------------------------------------------------
//  Strings
//
//  Every string lives here with its English text, so the component renders
//  correctly before the locale files are updated and the merger has one object
//  to copy from. Keys are the i18n keys; `{{x}}` is i18next interpolation.
// ---------------------------------------------------------------------------

export const EVIDENCE_STRINGS = {
  'evidence.title': 'Evidence',
  'evidence.subtitle': 'Files and links that support this {{target}}.',
  'evidence.targetAnswer': 'answer',
  'evidence.targetPlan': 'plan',
  'evidence.targetControl': 'control',
  'evidence.none': 'No evidence attached yet.',
  'evidence.noneReadOnly': 'No evidence was attached.',
  'evidence.readOnlyNote': 'This record is locked. Evidence can be viewed, downloaded and verified, but not changed.',
  'evidence.answerFirst': 'Choose an answer first, then you can attach evidence to it.',
  'evidence.loadFailed': 'Could not load the evidence list.',
  'evidence.countOfMax': '{{count}} of {{max}} used',
  'evidence.uploadedBy': 'Uploaded by {{name}}',
  'evidence.unknownUser': 'unknown user',
  'evidence.link': 'Link',
  'evidence.file': 'File',
  // Upload
  'evidence.dropHere': 'Drop files here, or click to choose',
  'evidence.tapToChoose': 'Tap to choose files',
  'evidence.dropActive': 'Release to upload',
  'evidence.limits': 'PDF, DOC, DOCX, XLS, XLSX, JPG or PNG. Up to {{size}} each, {{max}} items in total.',
  'evidence.limitsNote': 'These limits are checked here to save you time. The server checks the real contents of every file again and has the final say.',
  'evidence.full': 'The limit of {{max}} items has been reached. Remove one to add another.',
  'evidence.uploading': 'Uploading',
  'evidence.processing': 'Checking file…',
  'evidence.queued': 'Waiting',
  'evidence.uploadedOk': 'Uploaded',
  'evidence.dismiss': 'Dismiss',
  'evidence.dismissAll': 'Clear messages',
  'evidence.rejectedOthersContinue': 'Other files are unaffected.',
  'evidence.clientNotAllowed': '“{{name}}” is not an accepted file type.',
  'evidence.clientTooBig': '“{{name}}” is larger than the {{max}} limit.',
  'evidence.clientEmpty': '“{{name}}” is empty.',
  'evidence.clientNoRoom': '“{{name}}” was not uploaded: the limit of {{max}} items is reached.',
  // Actions
  'evidence.download': 'Download',
  'evidence.downloading': 'Downloading…',
  'evidence.open': 'Open link',
  'evidence.delete': 'Remove',
  'evidence.deleteTitle': 'Remove this evidence?',
  'evidence.deleteBody': '“{{name}}” will be permanently removed from this record. The removal is recorded in the audit trail.',
  'evidence.deleteConfirm': 'Remove evidence',
  'evidence.downloadBlocked': 'Download blocked: this file no longer matches the checksum recorded at upload, so it was not served. Verify its integrity and tell an administrator.',
  // Link
  'evidence.addLink': 'Add a link',
  'evidence.linkUrl': 'Link address (https:// or http://)',
  'evidence.linkLabel': 'Label (optional)',
  'evidence.linkAdd': 'Add link',
  'evidence.linkBadUrl': 'Enter a full address that starts with https:// or http://.',
  'evidence.linkNote': 'Only http and https links are accepted. A link points at something outside this system, so it cannot be integrity-checked.',
  // Verify
  'evidence.verify': 'Verify integrity',
  'evidence.verifyAll': 'Verify all files',
  'evidence.verifying': 'Verifying…',
  'evidence.verifyIntact': 'Unchanged since upload',
  'evidence.verifyIntactDetail': 'The stored file was hashed just now and its SHA-256 matches the checksum recorded when it was uploaded on {{date}}.',
  'evidence.verifyBroken': 'MODIFIED OR DAMAGED',
  'evidence.verifyBrokenDetail': 'The stored file does NOT match the checksum recorded when it was uploaded on {{date}}. It has been altered or damaged since. Do not rely on it as evidence, and report this to an administrator.',
  'evidence.verifyUnverifiable': 'Cannot be verified',
  'evidence.verifyUnverifiableDetail': 'No stored file or checksum exists for this item, so there is nothing to compare.',
  'evidence.verifyFailed': 'The check itself could not be completed. This says nothing about the file; try again.',
  'evidence.verifyCheckedAt': 'Checked {{time}}',
  'evidence.checksum': 'SHA-256 recorded at upload',
  'evidence.verifyWhatTitle': 'What does this prove?',
  'evidence.verifyWhatBody': 'It proves the stored bytes are identical to what was uploaded. It does not prove the content is true or complete. The checksum is also written to the audit trail at upload, which is the independent record to compare against.',
  // Error codes that have no shared translation yet
  'evidence.error.UPLOAD_NO_FILE': 'No file was received.',
  'evidence.error.UPLOAD_EMPTY': 'The file is empty.',
  'evidence.error.UPLOAD_TOO_LARGE': 'The file is larger than the {{max}} limit.',
  'evidence.error.PAYLOAD_TOO_LARGE': 'The file is larger than the {{max}} limit.',
  'evidence.error.UPLOAD_EXTENSION_NOT_ALLOWED': 'This file type is not accepted. Use PDF, DOC, DOCX, XLS, XLSX, JPG or PNG.',
  'evidence.error.UPLOAD_CONTENT_UNRECOGNISED': 'The contents of this file are not a recognised document or image, whatever its name says.',
  'evidence.error.UPLOAD_CONTENT_MISMATCH': 'The contents of this file do not match its extension, so it was rejected.',
  'evidence.error.EVIDENCE_TOO_MANY_FILES': 'The limit of {{max}} items is already reached.',
  'evidence.error.EVIDENCE_INTEGRITY_FAILED': 'This file no longer matches its recorded checksum and was not served.',
  'evidence.error.EVIDENCE_NOT_FOUND': 'This evidence no longer exists.',
  'evidence.error.ANSWER_NOT_FOUND': 'This answer no longer exists.',
  'evidence.error.PLAN_NOT_FOUND': 'This plan no longer exists.',
  'evidence.error.NETWORK': 'The connection dropped before the upload finished.',
  // Validity. The labels and the banner live in EvidenceLifecycle.tsx
  // (EVIDENCE_LIFECYCLE_STRINGS) because the assessment screen needs them too.
  'evidence.validityPrompt': 'Set a “valid until” date on anything that expires — a certificate, a penetration test, a signed DPA — and this panel will warn you before it lapses.',
} as const;

type EvidenceKey = keyof typeof EVIDENCE_STRINGS;

// ---------------------------------------------------------------------------
//  Types
// ---------------------------------------------------------------------------

interface EvidenceItem {
  id: string;
  kind: 'FILE' | 'LINK';
  originalName: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  url: string | null;
  checksum: string | null;
  createdAt: string;
  /** Validity window. Both nullable: most evidence has no meaningful expiry. */
  validFrom: string | null;
  validUntil: string | null;
  /**
   * DERIVED by the server, not stored. Trusted from the response rather than
   * recomputed here, because the warning window is a deployment setting the
   * browser cannot know — a client guessing 30 days against a server configured
   * for 60 would disagree with the notifications the same user receives.
   */
  lifecycleState: EvidenceLifecycleState;
  daysUntilExpiry: number | null;
  uploadedBy: { firstName: string; lastName: string } | null;
}

/**
 * The list response.
 *
 * This used to be a bare array. It carries the lifecycle roll-up now because
 * the "is this claim still supported?" verdict has to be decided in one place —
 * see the note at the top of EvidenceLifecycle.tsx.
 */
interface EvidenceListResponse {
  items: EvidenceItem[];
  summary: EvidenceLifecycleSummary;
  warningDays: number;
}

interface VerifyResponse {
  verifiable: boolean;
  intact: boolean | null;
  checksum?: string;
  uploadedAt?: string;
}

/**
 * Exactly one target. The union makes "neither" and "both" unrepresentable.
 *
 * The third member is what makes "prove once, satisfy many" real in the UI:
 * evidence attached to this organisation's row for a cross-framework catalogue
 * control supports every framework requirement mapped to that control, so the
 * restore-test report is uploaded ONCE rather than once per framework.
 */
type Target =
  | { answerId: string; planId?: never; organizationControlId?: never }
  | { planId: string; answerId?: never; organizationControlId?: never }
  | { organizationControlId: string; answerId?: never; planId?: never };
export type EvidencePanelProps = Target & {
  /** True when the record is locked or the viewer may not change it. */
  readOnly?: boolean;
};

type VerifyState =
  | { status: 'running' }
  | { status: 'done'; result: VerifyResponse; checkedAt: Date }
  | { status: 'error' };

interface UploadRow {
  key: string;
  name: string;
  size: number;
  status: 'queued' | 'uploading' | 'processing' | 'error';
  progress: number;
  error?: string;
}

// ---------------------------------------------------------------------------
//  Client-side courtesy validation
// ---------------------------------------------------------------------------

/** Every extension the server accepts, derived from the shared list so the two cannot drift. */
const ALLOWED_EXTENSIONS: string[] = ALLOWED_UPLOAD_TYPES.flatMap((type) => [...type.ext]);
const ACCEPT_ATTRIBUTE = ALLOWED_EXTENSIONS.map((e) => `.${e}`).join(',');

function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot < 0 ? '' : name.slice(dot + 1).toLowerCase();
}

function formatBytes(bytes: number, locale: string): string {
  const nf = (n: number) => new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(n);
  if (bytes < 1024) return `${nf(bytes)} B`;
  if (bytes < 1024 * 1024) return `${nf(bytes / 1024)} KB`;
  return `${nf(bytes / (1024 * 1024))} MB`;
}

/** Only ever hand a real http(s) URL to an href, even though the server enforces it too. */
function isHttpUrl(value: string | null): value is string {
  if (!value) return false;
  try {
    const u = new URL(value);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
//  Authenticated transport
// ---------------------------------------------------------------------------

async function toApiError(response: Response): Promise<ApiError> {
  const payload = (await response.json().catch(() => null)) as
    | { error?: { code: string; details?: Record<string, unknown> } }
    | null;
  return new ApiError(response.status, payload?.error?.code ?? 'UNKNOWN_ERROR', payload?.error?.details);
}

/**
 * Upload one file with progress.
 *
 * XMLHttpRequest, not fetch: fetch still cannot report UPLOAD progress. It is
 * the one place this app reaches for XHR, and only because of that.
 *
 * The target id is appended BEFORE the file. The server reads the multipart
 * stream in order and only sees fields that arrived ahead of the file part;
 * appended after, the id would silently be missing and the upload would be
 * refused with "target required".
 */
function xhrUpload(
  file: File,
  target: Target,
  onProgress: (fraction: number) => void,
  register: (abort: () => void) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    if (target.answerId) form.append('answerId', target.answerId);
    if (target.planId) form.append('planId', target.planId);
    if (target.organizationControlId) {
      form.append('organizationControlId', target.organizationControlId);
    }
    form.append('file', file, file.name);

    const xhr = new XMLHttpRequest();
    register(() => xhr.abort());
    xhr.open('POST', '/api/evidence/upload');
    const token = getAccessToken();
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.withCredentials = true;

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onerror = () => reject(new ApiError(0, 'NETWORK'));
    xhr.onabort = () => reject(new ApiError(0, 'ABORTED'));
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) return resolve();
      const parse = (): { error?: { code: string; details?: Record<string, unknown> } } | null => {
        try {
          return JSON.parse(xhr.responseText);
        } catch {
          return null; // non-JSON error body: fall through to UNKNOWN_ERROR
        }
      };
      const payload = parse();
      reject(new ApiError(xhr.status, payload?.error?.code ?? 'UNKNOWN_ERROR', payload?.error?.details));
    };
    xhr.send(form);
  });
}

// ---------------------------------------------------------------------------
//  Component
// ---------------------------------------------------------------------------

export function EvidencePanel(props: EvidencePanelProps) {
  const { answerId, planId, organizationControlId, readOnly = false } = props;
  const target = (
    answerId ? { answerId } : planId ? { planId } : { organizationControlId: organizationControlId! }
  ) as Target;

  const { t, i18n } = useTranslation();
  const { allows } = useAuth();
  const queryClient = useQueryClient();
  const describeApiError = useApiError();

  /** Translate with the built-in English as the fallback. */
  const tx = useCallback(
    (key: EvidenceKey, options?: Record<string, unknown>) =>
      t(key, { defaultValue: EVIDENCE_STRINGS[key], ...options }),
    [t],
  );

  const locale = i18n.language;
  const maxSize = formatBytes(MAX_UPLOAD_BYTES, locale);

  const canUpload = !readOnly && allows('evidence.upload');
  const canDelete = !readOnly && allows('evidence.delete');

  const queryKey = [
    'evidence',
    {
      answerId: answerId ?? null,
      planId: planId ?? null,
      organizationControlId: organizationControlId ?? null,
    },
  ] as const;
  const listPath = `/evidence${qs({ answerId, planId, organizationControlId })}`;

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey,
    queryFn: () => api.get<EvidenceListResponse>(listPath),
  });
  const items = data?.items;

  // --- Error text -----------------------------------------------------------

  /**
   * Prefer this panel's own message for a code (they carry the file limits);
   * otherwise fall back to the app-wide translation.
   */
  const errorText = useCallback(
    (err: unknown): string => {
      if (err instanceof ApiError) {
        const key = `evidence.error.${err.code}` as EvidenceKey;
        if (key in EVIDENCE_STRINGS) {
          const details = err.details ?? {};
          return tx(key, { ...details, max: err.code === 'EVIDENCE_TOO_MANY_FILES'
            ? MAX_EVIDENCE_FILES_PER_ANSWER
            : maxSize });
        }
      }
      return describeApiError(err);
    },
    [tx, describeApiError, maxSize],
  );

  // --- Authenticated fetch (used for download) -------------------------------

  /**
   * GET with the bearer token. On a 401 it lets the shared client refresh the
   * session (by making one cheap authenticated call, which runs the app's
   * single-flight refresh) and retries once — so an expired token does not turn
   * into a mysterious failed download.
   */
  const authedGet = useCallback(
    async (path: string): Promise<Response> => {
      const attempt = () => {
        const token = getAccessToken();
        return fetch(path, {
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
      };
      let response = await attempt();
      if (response.status === 401) {
        await api.get(listPath); // triggers refresh-and-replay inside the api client
        response = await attempt();
      }
      return response;
    },
    [listPath],
  );

  // --- Upload queue ----------------------------------------------------------

  const [rows, setRows] = useState<UploadRow[]>([]);
  const [dragging, setDragging] = useState(false);
  const dragDepth = useRef(0);
  const fileInput = useRef<HTMLInputElement>(null);
  const aborters = useRef<Set<() => void>>(new Set());
  const mounted = useRef(true);
  // The list length as the server will count it, kept current for the loop below.
  const knownCount = useRef(0);
  knownCount.current = items?.length ?? 0;

  useEffect(() => {
    mounted.current = true;
    const live = aborters.current;
    return () => {
      mounted.current = false;
      // Abandoned uploads must not keep running (and mutating state) after unmount.
      live.forEach((abort) => abort());
    };
  }, []);

  const patchRow = (key: string, patch: Partial<UploadRow>) => {
    if (!mounted.current) return;
    setRows((current) => current.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  /**
   * Process a batch.
   *
   * Sequential on purpose. The server enforces the item limit by counting what
   * is stored at the moment each request arrives, so parallel uploads could all
   * pass the check and overshoot it. Sequential also means a rejection of one
   * file (each is its own request) leaves every other file untouched.
   */
  const uploadFiles = async (files: File[]) => {
    if (files.length === 0) return;

    const batch: Array<{ file: File; row: UploadRow }> = [];
    const rejected: UploadRow[] = [];
    let slots = MAX_EVIDENCE_FILES_PER_ANSWER - knownCount.current;

    files.forEach((file, i) => {
      const key = `${Date.now()}-${i}-${file.name}`;
      const base = { key, name: file.name, size: file.size, progress: 0 };
      const ext = extensionOf(file.name);
      let problem: string | null = null;
      if (!ALLOWED_EXTENSIONS.includes(ext)) problem = tx('evidence.clientNotAllowed', { name: file.name });
      else if (file.size === 0) problem = tx('evidence.clientEmpty', { name: file.name });
      else if (file.size > MAX_UPLOAD_BYTES) {
        problem = tx('evidence.clientTooBig', { name: file.name, max: maxSize });
      } else if (slots <= 0) {
        problem = tx('evidence.clientNoRoom', { name: file.name, max: MAX_EVIDENCE_FILES_PER_ANSWER });
      }

      if (problem) {
        rejected.push({ ...base, status: 'error', error: problem });
      } else {
        slots -= 1;
        batch.push({ file, row: { ...base, status: 'queued' } });
      }
    });

    setRows((current) => [
      ...current.filter((r) => r.status !== 'error'), // a new batch replaces old error messages
      ...rejected,
      ...batch.map((b) => b.row),
    ]);

    for (const { file, row } of batch) {
      if (!mounted.current) return;
      patchRow(row.key, { status: 'uploading' });
      try {
        const run = async () => {
          let abort: () => void = () => {};
          try {
            await xhrUpload(
              file,
              target,
              (fraction) => patchRow(row.key, {
                progress: Math.round(fraction * 100),
                // Bytes are all sent but the server is still validating and storing.
                ...(fraction >= 1 ? { status: 'processing' as const } : {}),
              }),
              (a) => { abort = a; aborters.current.add(a); },
            );
          } finally {
            aborters.current.delete(abort);
          }
        };
        try {
          await run();
        } catch (err) {
          // Expired token: refresh through the shared client, then retry once.
          if (err instanceof ApiError && err.status === 401) {
            await api.get(listPath);
            await run();
          } else {
            throw err;
          }
        }
        if (!mounted.current) return;
        // Finished files leave the queue; they now appear in the real list.
        setRows((current) => current.filter((r) => r.key !== row.key));
        await queryClient.invalidateQueries({ queryKey: ['evidence'] });
      } catch (err) {
        if (err instanceof ApiError && err.code === 'ABORTED') return;
        // Only this file fails. The loop moves on to the next.
        patchRow(row.key, { status: 'error', error: errorText(err) });
      }
    }
  };

  const onPick = (list: FileList | null) => {
    if (list) void uploadFiles(Array.from(list));
    // Reset so choosing the same file again still fires `change`.
    if (fileInput.current) fileInput.current.value = '';
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    void uploadFiles(Array.from(e.dataTransfer.files));
  };

  // dragenter/leave fire for every child element crossed, so a naive boolean
  // flickers. A depth counter is the reliable way to know when the drag has
  // truly left the zone.
  const onDragEnter = (e: DragEvent) => {
    e.preventDefault();
    dragDepth.current += 1;
    setDragging(true);
  };
  const onDragLeave = () => {
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  };

  const onZoneKey = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fileInput.current?.click();
    }
  };

  // --- Download ---------------------------------------------------------------

  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  // `itemId` pins the message next to the item it is about: with several items
  // listed, a notice at the top of the panel would be off-screen when the
  // Download button that caused it was tapped.
  const [actionError, setActionErrorState] = useState<{ itemId?: string; message: string } | null>(null);
  const setActionError = (message: string | null, itemId?: string) =>
    setActionErrorState(message ? { message, ...(itemId ? { itemId } : {}) } : null);

  const download = async (item: EvidenceItem) => {
    setActionError(null);
    setDownloadingId(item.id);
    try {
      const response = await authedGet(`/api/evidence/${item.id}/download`);
      if (!response.ok) throw await toApiError(response);
      const blob = await response.blob();

      const objectUrl = URL.createObjectURL(blob);
      try {
        const a = document.createElement('a');
        a.href = objectUrl;
        // The suggested filename is set by us from the stored label; the server
        // also forces Content-Disposition: attachment, so nothing renders here.
        a.download = item.originalName ?? 'evidence';
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
      } finally {
        // Revoke, or the Blob lives until the page is closed. Deferred one
        // second because Firefox starts reading the URL asynchronously and a
        // synchronous revoke can cancel the download.
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === 'EVIDENCE_INTEGRITY_FAILED') {
        setActionError(tx('evidence.downloadBlocked'), item.id);
      } else {
        setActionError(errorText(err), item.id);
      }
    } finally {
      setDownloadingId(null);
    }
  };

  // --- Verify -----------------------------------------------------------------

  const [verifyState, setVerifyState] = useState<Record<string, VerifyState>>({});

  const verify = async (item: EvidenceItem) => {
    setVerifyState((s) => ({ ...s, [item.id]: { status: 'running' } }));
    try {
      const result = await api.get<VerifyResponse>(`/evidence/${item.id}/verify`);
      if (!mounted.current) return;
      setVerifyState((s) => ({ ...s, [item.id]: { status: 'done', result, checkedAt: new Date() } }));
    } catch {
      if (!mounted.current) return;
      setVerifyState((s) => ({ ...s, [item.id]: { status: 'error' } }));
    }
  };

  const verifyAll = async () => {
    for (const item of items ?? []) {
      if (item.kind === 'FILE') await verify(item);
    }
  };

  // --- Delete -----------------------------------------------------------------

  const [pendingDelete, setPendingDelete] = useState<EvidenceItem | null>(null);
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/evidence/${id}`),
    onSuccess: () => {
      setPendingDelete(null);
      setActionError(null);
      void queryClient.invalidateQueries({ queryKey: ['evidence'] });
    },
    onError: (err) => {
      setPendingDelete(null);
      setActionError(errorText(err));
    },
  });

  // --- Link -------------------------------------------------------------------

  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkLabel, setLinkLabel] = useState('');
  const [linkError, setLinkError] = useState<string | null>(null);

  const addLink = useMutation({
    mutationFn: () =>
      api.post('/evidence/link', {
        ...target,
        url: linkUrl.trim(),
        ...(linkLabel.trim() ? { label: linkLabel.trim() } : {}),
      }),
    onSuccess: () => {
      setLinkUrl('');
      setLinkLabel('');
      setLinkOpen(false);
      setLinkError(null);
      void queryClient.invalidateQueries({ queryKey: ['evidence'] });
    },
    onError: (err) => setLinkError(errorText(err)),
  });

  const submitLink = () => {
    setLinkError(null);
    // Same rule as the server: http(s) only. `javascript:` / `data:` would be a
    // stored-XSS vector for whoever later clicks it.
    if (!/^https?:\/\//i.test(linkUrl.trim()) || !isHttpUrl(linkUrl.trim())) {
      setLinkError(tx('evidence.linkBadUrl'));
      return;
    }
    addLink.mutate();
  };

  // --- Render ------------------------------------------------------------------

  const count = items?.length ?? 0;
  const full = count >= MAX_EVIDENCE_FILES_PER_ANSWER;
  const hasFiles = (items ?? []).some((i) => i.kind === 'FILE');
  const fmtDateTime = (iso: string) =>
    new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso));
  const errorRows = rows.filter((r) => r.status === 'error');

  return (
    <section aria-labelledby={`evidence-h-${answerId ?? planId ?? organizationControlId}`} className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 id={`evidence-h-${answerId ?? planId ?? organizationControlId}`} className="text-sm font-semibold text-ink">
            {tx('evidence.title')}
          </h3>
          <p className="text-xs text-ink-muted">
            {tx('evidence.subtitle', {
              target: tx(
                answerId
                  ? 'evidence.targetAnswer'
                  : planId
                    ? 'evidence.targetPlan'
                    : 'evidence.targetControl',
              ),
            })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="oc-pill-neutral tabular-nums">
            {tx('evidence.countOfMax', { count, max: MAX_EVIDENCE_FILES_PER_ANSWER })}
          </span>
          {hasFiles && (
            <Button size="sm" onClick={() => void verifyAll()}>
              {tx('evidence.verifyAll')}
            </Button>
          )}
        </div>
      </div>

      {readOnly && (
        <p className="rounded-xl bg-surface-sunken px-3 py-2 text-xs text-ink-secondary">
          {tx('evidence.readOnlyNote')}
        </p>
      )}

      {actionError && !actionError.itemId && <ErrorNotice message={actionError.message} />}

      {/* --- Expiry, before anything else ---
          Above the list on purpose. "Three files are attached" is reassuring
          and, when all three lapsed in June, wrong — so the correction has to
          arrive before the reassurance, not after it. */}
      <EvidenceExpiryAlert summary={data?.summary} warningDays={data?.warningDays} />

      {/* --- List --- */}
      {isLoading ? (
        <div className="space-y-2">{[0, 1].map((i) => <Skeleton key={i} className="h-16" />)}</div>
      ) : isError ? (
        <ErrorNotice message={tx('evidence.loadFailed')} onRetry={() => void refetch()} retryLabel={t('common.retry')} />
      ) : items && items.length === 0 && rows.length === 0 ? (
        <p className="text-sm text-ink-muted py-1">
          {readOnly ? tx('evidence.noneReadOnly') : tx('evidence.none')}
        </p>
      ) : (
        <ul className="space-y-2">
          {(items ?? []).map((item) => {
            const state = verifyState[item.id];
            const uploader = item.uploadedBy
              ? `${item.uploadedBy.firstName} ${item.uploadedBy.lastName}`
              : tx('evidence.unknownUser');
            return (
              <li key={item.id} className="rounded-xl border border-line p-3">
                <div className="flex items-start gap-3">
                  <span
                    aria-hidden
                    className="mt-0.5 shrink-0 w-8 h-8 rounded-lg bg-brand/10 text-brand grid place-items-center"
                  >
                    {item.kind === 'LINK' ? <LinkGlyph /> : <FileGlyph />}
                  </span>
                  <div className="min-w-0 flex-1">
                    {/* break-all: filenames and URLs have no spaces to wrap on. */}
                    <p className="text-sm font-medium text-ink break-all leading-snug">
                      {item.originalName ?? item.url}
                    </p>
                    <p className="text-xs text-ink-muted mt-0.5">
                      {item.kind === 'LINK' ? tx('evidence.link') : formatBytes(item.sizeBytes ?? 0, locale)}
                      {' · '}
                      {tx('evidence.uploadedBy', { name: uploader })}
                      {' · '}
                      <time dateTime={item.createdAt}>{fmtDateTime(item.createdAt)}</time>
                    </p>
                    {item.kind === 'LINK' && item.url && item.url !== item.originalName && (
                      <p className="text-xs text-ink-secondary break-all mt-0.5">{item.url}</p>
                    )}
                    {/* Validity sits with the item, not in a separate column:
                        the question "is this still good?" is about this
                        document, and the answer has to be where the document
                        is named. */}
                    <div className="mt-1.5">
                      <LifecycleBadge item={item} />
                    </div>
                  </div>
                </div>

                <div className="mt-2.5 flex flex-wrap gap-1.5">
                  {item.kind === 'FILE' ? (
                    <>
                      <Button size="sm" disabled={downloadingId === item.id} onClick={() => void download(item)}>
                        {downloadingId === item.id ? tx('evidence.downloading') : tx('evidence.download')}
                      </Button>
                      <Button
                        size="sm"
                        disabled={state?.status === 'running'}
                        onClick={() => void verify(item)}
                      >
                        {state?.status === 'running' ? tx('evidence.verifying') : tx('evidence.verify')}
                      </Button>
                    </>
                  ) : isHttpUrl(item.url) ? (
                    // noopener + noreferrer: the linked page must not get a
                    // handle on this window (window.opener) or see our URL.
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="oc-btn-secondary oc-btn-sm"
                    >
                      {tx('evidence.open')}
                    </a>
                  ) : null}
                  {/* Gated on `evidence.upload`, like attaching: dating the
                      document you attached is part of attaching it. */}
                  <ValidityEditor
                    evidenceId={item.id}
                    validFrom={item.validFrom}
                    validUntil={item.validUntil}
                    readOnly={!canUpload}
                  />
                  {canDelete && (
                    <Button size="sm" variant="ghost" onClick={() => setPendingDelete(item)}>
                      {tx('evidence.delete')}
                    </Button>
                  )}
                </div>

                {actionError?.itemId === item.id && (
                  <div className="mt-2.5"><ErrorNotice message={actionError.message} /></div>
                )}

                {state && state.status !== 'running' && (
                  <VerifyResult state={state} tx={tx} fmt={fmtDateTime} locale={locale} />
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* --- Add --- */}
      {canUpload && (
        <div className="space-y-2">
          {full ? (
            <p className="rounded-xl bg-surface-sunken px-3 py-2 text-xs text-ink-secondary">
              {tx('evidence.full', { max: MAX_EVIDENCE_FILES_PER_ANSWER })}
            </p>
          ) : (
            <div
              role="button"
              tabIndex={0}
              onClick={() => fileInput.current?.click()}
              onKeyDown={onZoneKey}
              onDragEnter={onDragEnter}
              onDragOver={(e) => e.preventDefault()}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              className={cn(
                'rounded-xl border-2 border-dashed px-4 py-5 text-center cursor-pointer transition-colors',
                'min-h-[5rem] flex flex-col items-center justify-center gap-1',
                dragging ? 'border-brand bg-brand/[0.06]' : 'border-line hover:bg-surface-sunken',
              )}
            >
              <span className="text-sm font-medium text-ink">
                {dragging ? (
                  tx('evidence.dropActive')
                ) : (
                  <>
                    {/* No drag-and-drop on a phone, so do not tell a phone user to drag. */}
                    <span className="sm:hidden">{tx('evidence.tapToChoose')}</span>
                    <span className="hidden sm:inline">{tx('evidence.dropHere')}</span>
                  </>
                )}
              </span>
              <span className="text-xs text-ink-muted">
                {tx('evidence.limits', { size: maxSize, max: MAX_EVIDENCE_FILES_PER_ANSWER })}
              </span>
              <input
                ref={fileInput}
                type="file"
                multiple
                accept={ACCEPT_ATTRIBUTE}
                className="sr-only"
                tabIndex={-1}
                aria-label={tx('evidence.dropHere')}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => onPick(e.target.files)}
              />
            </div>
          )}
          <p className="text-[11px] leading-snug text-ink-muted">{tx('evidence.limitsNote')}</p>
          {/* Shown only once something is attached: on an empty panel the first
              job is to attach a file, and advice about expiry dates for files
              that do not exist yet is noise. */}
          {count > 0 && (
            <p className="text-[11px] leading-snug text-ink-muted">{tx('evidence.validityPrompt')}</p>
          )}

          {/* Per-file progress and errors. One failure leaves the others alone. */}
          {rows.length > 0 && (
            <ul className="space-y-2" aria-live="polite">
              {rows.map((row) => (
                <li
                  key={row.key}
                  className={cn(
                    'rounded-xl border p-3',
                    row.status === 'error' ? 'border-critical/30 bg-critical/[0.05]' : 'border-line',
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm text-ink break-all min-w-0">
                      {row.name}
                      <span className="text-xs text-ink-muted"> · {formatBytes(row.size, locale)}</span>
                    </p>
                    {row.status === 'error' && (
                      <button
                        className="text-xs font-medium text-brand hover:underline shrink-0 py-1"
                        onClick={() => setRows((c) => c.filter((r) => r.key !== row.key))}
                      >
                        {tx('evidence.dismiss')}
                      </button>
                    )}
                  </div>
                  {row.status === 'error' ? (
                    <p role="alert" className="mt-1 text-xs text-critical">{row.error}</p>
                  ) : row.status === 'queued' ? (
                    <p className="mt-1 text-xs text-ink-muted">{tx('evidence.queued')}</p>
                  ) : (
                    <div className="mt-2">
                      <ProgressBar value={row.progress} tone="brand" label={row.name} />
                      {row.status === 'processing' && (
                        <p className="mt-1 text-xs text-ink-muted flex items-center gap-1.5">
                          <Spinner className="h-3.5 w-3.5" /> {tx('evidence.processing')}
                        </p>
                      )}
                    </div>
                  )}
                </li>
              ))}
              {errorRows.length > 1 && (
                <li>
                  <button
                    className="text-xs font-medium text-brand hover:underline py-1"
                    onClick={() => setRows((c) => c.filter((r) => r.status !== 'error'))}
                  >
                    {tx('evidence.dismissAll')}
                  </button>
                </li>
              )}
            </ul>
          )}

          {/* Link */}
          {!linkOpen ? (
            <Button size="sm" variant="ghost" onClick={() => setLinkOpen(true)}>
              + {tx('evidence.addLink')}
            </Button>
          ) : (
            <form
              className="rounded-xl border border-line p-3 space-y-2"
              onSubmit={(e) => { e.preventDefault(); submitLink(); }}
              noValidate
            >
              <label className="oc-label" htmlFor={`ev-url-${answerId ?? planId ?? organizationControlId}`}>
                {tx('evidence.linkUrl')}
              </label>
              <input
                id={`ev-url-${answerId ?? planId ?? organizationControlId}`}
                className="oc-input"
                type="url"
                inputMode="url"
                autoComplete="off"
                maxLength={2000}
                placeholder="https://"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
              />
              <label className="oc-label" htmlFor={`ev-label-${answerId ?? planId ?? organizationControlId}`}>
                {tx('evidence.linkLabel')}
              </label>
              <input
                id={`ev-label-${answerId ?? planId ?? organizationControlId}`}
                className="oc-input"
                type="text"
                maxLength={200}
                value={linkLabel}
                onChange={(e) => setLinkLabel(e.target.value)}
              />
              {linkError && <p role="alert" className="text-xs text-critical">{linkError}</p>}
              <p className="text-[11px] leading-snug text-ink-muted">{tx('evidence.linkNote')}</p>
              <div className="flex gap-2">
                <Button type="submit" variant="primary" size="sm" disabled={addLink.isPending || !linkUrl.trim()}>
                  {addLink.isPending ? t('common.saving') : tx('evidence.linkAdd')}
                </Button>
                <Button size="sm" onClick={() => { setLinkOpen(false); setLinkError(null); }}>
                  {t('common.cancel')}
                </Button>
              </div>
            </form>
          )}
        </div>
      )}

      <Modal
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        title={tx('evidence.deleteTitle')}
        footer={
          <>
            <Button onClick={() => setPendingDelete(null)}>{t('common.cancel')}</Button>
            <Button
              variant="danger"
              disabled={remove.isPending}
              onClick={() => pendingDelete && remove.mutate(pendingDelete.id)}
            >
              {remove.isPending ? t('common.saving') : tx('evidence.deleteConfirm')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink-secondary break-words">
          {tx('evidence.deleteBody', { name: pendingDelete?.originalName ?? pendingDelete?.url ?? '' })}
        </p>
      </Modal>
    </section>
  );
}

// ---------------------------------------------------------------------------
//  Verification result
// ---------------------------------------------------------------------------

/**
 * The integrity outcome.
 *
 * Three outcomes, each visually and textually distinct, because this is the
 * screen someone points at to say "this evidence was not tampered with":
 *   intact       -> green, check icon, "Unchanged since upload"
 *   NOT intact   -> red, warning icon, capitals, role="alert"
 *   unverifiable -> grey, "Cannot be verified" (never presented as a pass)
 * A failed REQUEST is a fourth state and is worded as "says nothing about the
 * file" so an outage is never mistaken for either verdict.
 */
function VerifyResult({ state, tx, fmt, locale }: {
  state: Exclude<VerifyState, { status: 'running' }>;
  tx: (key: EvidenceKey, options?: Record<string, unknown>) => string;
  fmt: (iso: string) => string;
  locale: string;
}) {
  if (state.status === 'error') {
    return (
      <p role="alert" className="mt-2.5 rounded-lg bg-surface-sunken px-3 py-2 text-xs text-ink-secondary">
        {tx('evidence.verifyFailed')}
      </p>
    );
  }

  const { result, checkedAt } = state;
  const uploadedOn = result.uploadedAt ? fmt(result.uploadedAt) : '';
  const checked = tx('evidence.verifyCheckedAt', {
    time: new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'medium' }).format(checkedAt),
  });

  let tone: 'good' | 'critical' | 'neutral';
  let heading: string;
  let detail: string;
  if (!result.verifiable || result.intact === null) {
    tone = 'neutral';
    heading = tx('evidence.verifyUnverifiable');
    detail = tx('evidence.verifyUnverifiableDetail');
  } else if (result.intact) {
    tone = 'good';
    heading = tx('evidence.verifyIntact');
    detail = tx('evidence.verifyIntactDetail', { date: uploadedOn });
  } else {
    tone = 'critical';
    heading = tx('evidence.verifyBroken');
    detail = tx('evidence.verifyBrokenDetail', { date: uploadedOn });
  }

  return (
    <div
      role={tone === 'critical' ? 'alert' : 'status'}
      className={cn(
        'mt-2.5 rounded-lg border px-3 py-2.5',
        tone === 'good' && 'border-good/40 bg-good/[0.08]',
        tone === 'critical' && 'border-critical/50 bg-critical/[0.09]',
        tone === 'neutral' && 'border-line bg-surface-sunken',
      )}
    >
      <div className="flex items-center gap-2">
        {tone === 'good' && <span className="text-good"><CheckGlyph /></span>}
        {tone === 'critical' && <span className="text-critical"><WarnGlyph /></span>}
        <p
          className={cn(
            'text-sm font-semibold',
            tone === 'good' && 'text-good',
            tone === 'critical' && 'text-critical',
            tone === 'neutral' && 'text-ink-secondary',
          )}
        >
          {heading}
        </p>
      </div>
      <p className="mt-1 text-xs text-ink-secondary leading-relaxed">{detail}</p>
      {result.checksum && (
        <p className="mt-1.5 text-[11px] text-ink-muted">
          {tx('evidence.checksum')}
          <code className="block font-mono break-all text-ink-secondary mt-0.5">{result.checksum}</code>
        </p>
      )}
      <p className="mt-1.5 text-[11px] text-ink-muted">{checked}</p>
      <details className="mt-1.5">
        <summary className="text-[11px] text-brand cursor-pointer hover:underline">
          {tx('evidence.verifyWhatTitle')}
        </summary>
        <p className="mt-1 text-[11px] leading-relaxed text-ink-secondary">{tx('evidence.verifyWhatBody')}</p>
      </details>
    </div>
  );
}

// --- Glyphs (kept local: used nowhere else) -----------------------------------

const glyph = { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.75,
  strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true, className: 'w-4 h-4' } as const;

const FileGlyph = () => (
  <svg {...glyph}><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z" /><path d="M14 3v5h5" /></svg>
);
const LinkGlyph = () => (
  <svg {...glyph}><path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" /><path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" /></svg>
);
const CheckGlyph = () => (
  <svg {...glyph} className="w-5 h-5"><circle cx="12" cy="12" r="9" /><path d="m8 12 3 3 5-6" /></svg>
);
const WarnGlyph = () => (
  <svg {...glyph} className="w-5 h-5"><path d="M12 3 2 20h20L12 3Z" /><path d="M12 10v4" /><path d="M12 17.5v.01" /></svg>
);
